// 科大讯飞在线语音合成 (TTS) —— wss://tts-api.xfyun.cn/v2/tts
// 用于「词阅工坊」AI 阅读文章朗读。浏览器原生 speechSynthesis 在部分环境无声，
// 改用讯飞 TTS：WebSocket + HmacSHA256 鉴权，返回 mp3 音频分片，拼接后用 Audio 播放。

// 讯飞开放平台凭证（沿用旧项目）
export function getIFlyTek() {
  return {
    APISecret: 'YzliNTgzZjMxMzg3ODliNjI4MDczZTdk',
    APPID: '90abc295',
    APIKey: '7ba4aa6bb4edc14a36cbf14535100e04'
  }
}

// 用 Web Crypto 计算 HmacSHA256 并做 base64（替代 crypto-js，免依赖）
async function hmacSha256Base64(secret, message) {
  const enc = new TextEncoder()
  const key = await window.crypto.subtle.importKey(
    'raw',
    enc.encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  )
  const sig = await window.crypto.subtle.sign('HMAC', key, enc.encode(message))
  const bytes = new Uint8Array(sig)
  let bin = ''
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i])
  return window.btoa(bin)
}

// UTF-8 字符串 -> base64
function utf8ToBase64(str) {
  return window.btoa(unescape(encodeURIComponent(str)))
}

// base64 -> Uint8Array
function base64ToUint8Array(base64) {
  const bin = window.atob(base64)
  const len = bin.length
  const bytes = new Uint8Array(len)
  for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i)
  return bytes
}

// 构造带鉴权参数的 WebSocket 地址
async function buildWsUrl() {
  const { APIKey, APISecret } = getIFlyTek()
  const host = 'tts-api.xfyun.cn'
  const path = '/v2/tts'
  const date = new Date().toUTCString()
  const signatureOrigin = `host: ${host}\ndate: ${date}\nGET ${path} HTTP/1.1`
  const signature = await hmacSha256Base64(APISecret, signatureOrigin)
  const authorizationOrigin =
    `api_key="${APIKey}", algorithm="hmac-sha256", headers="host date request-line", signature="${signature}"`
  const authorization = window.btoa(authorizationOrigin)
  return `wss://${host}${path}?authorization=${encodeURIComponent(authorization)}` +
    `&date=${encodeURIComponent(date)}&host=${host}`
}

/**
 * 语音合成：把文本合成为 mp3 并返回可播放的 Blob URL
 * @param {string} text 待朗读文本
 * @param {object} options { vcn 发音人, speed 语速0-100, volume 音量0-100, pitch 语调0-100 }
 * @returns {Promise<string>} mp3 的 objectURL
 */
export async function synthesize(text, options = {}) {
  const {
    vcn = 'x4_EnUs_Laura_education', // 英文发音人，需在讯飞控制台开通
    speed = 50,
    volume = 50,
    pitch = 50
  } = options

  const { APPID } = getIFlyTek()
  const url = await buildWsUrl()

  return new Promise((resolve, reject) => {
    const ws = new WebSocket(url)
    const audioChunks = []

    const frame = {
      common: { app_id: APPID },
      business: {
        aue: 'lame',       // mp3
        sfl: 1,            // 流式返回 mp3
        auf: 'audio/L16;rate=16000',
        vcn,
        speed,
        volume,
        pitch,
        tte: 'UTF8'
      },
      data: {
        status: 2,
        text: utf8ToBase64(text)
      }
    }

    ws.onopen = () => ws.send(JSON.stringify(frame))

    ws.onmessage = (e) => {
      let res
      try {
        res = JSON.parse(e.data)
      } catch (err) {
        reject(err)
        ws.close()
        return
      }
      if (res.code !== 0) {
        reject(new Error(`讯飞TTS错误(${res.code}): ${res.message}`))
        ws.close()
        return
      }
      if (res.data && res.data.audio) {
        audioChunks.push(base64ToUint8Array(res.data.audio))
      }
      // status === 2 表示合成结束
      if (res.data && res.data.status === 2) {
        ws.close()
        const total = audioChunks.reduce((n, c) => n + c.length, 0)
        const merged = new Uint8Array(total)
        let offset = 0
        audioChunks.forEach((c) => { merged.set(c, offset); offset += c.length })
        const blob = new Blob([merged], { type: 'audio/mpeg' })
        resolve(URL.createObjectURL(blob))
      }
    }

    ws.onerror = (err) => reject(err)
  })
}
